import React, { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import AnnouncementIcon from '../announcement-icon';
import AnnouncementCard from '../announcement-card';
import EmptyState from '../empty-state/empty-state';
// import announcementsData from './announcements-test-data';
import { get, getText } from '../../../helpers';
import { dismissAnnouncements, markAnnouncementsViewed } from '../../../redux/announcements/announcements.actions';
import {
    hasAnonymousSessionReferenceLock,
    saveAnnouncementViewed,
    loadAnnouncementViewedState,
    loadAnonymousCurrentSessionNewIds,
    loadAnonymousLastOpenedTimestamp,
    loadAnonymousSessionReferenceTimestamp,
    lockAnonymousSessionReferenceTimestamp,
    saveAnonymousSessionReferenceTimestamp,
} from '../../../redux/announcements/announcements.storage';
import history from '../../../utilities/history';
import adjustPanelPosition from './whats-new-panel-helper';
import './whats-new-panel.style.scss';

const toTimestamp = (value: string): number | null => {
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

const isAnnouncementNew = (publishedAt: string, referenceTimestamp: number | null): boolean => {
    if (referenceTimestamp === null) {
        return true;
    }

    const publishedTimestamp = toTimestamp(publishedAt);
    if (publishedTimestamp === null) {
        return false;
    }

    return publishedTimestamp > referenceTimestamp;
};

function WhatsNewPanel() {
    // const notifications = announcementsData.Announcements || [];
    const dispatch = useDispatch();
    const notifications = useSelector((state: RootStoreState) => get(state, 'announcements.Announcements', []));

    const panelRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLDivElement>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
    const [announcementsViewed, setAnnouncementsViewed] = useState<boolean | null>(loadAnnouncementViewedState());
    const [anonReferenceTimestamp, setAnonReferenceTimestamp] = useState<number | null>(() => {
        const sessionReference = loadAnonymousSessionReferenceTimestamp();
        return sessionReference === null ? loadAnonymousLastOpenedTimestamp() : sessionReference;
    });
    const wn = 'whats-new';
    const langCode = getText('langCode');
    const isLoggedIn = useSelector((state: RootStoreState) => get(state, 'user.isLoggedIn', false));
    const showRedDot = useSelector((state: RootStoreState) => get(state, 'announcements.ShowRedDot', false));
    const hasNotifications = notifications.length > 0;
    const [currentSessionNewIds, setCurrentSessionNewIds] = useState<string[]>(() => loadAnonymousCurrentSessionNewIds());
    const isGuestAnnouncementNew = (item: Announcement, referenceTimestamp: number | null = anonReferenceTimestamp) => {
        return currentSessionNewIds.includes(item.KeyMarketplaceAnnouncements)
            || isAnnouncementNew(item.PublishedAt, referenceTimestamp);
    };
    const hasAnonymousUnread = hasNotifications && notifications.some((item: Announcement) => isGuestAnnouncementNew(item));
    const hasUnread = isLoggedIn ? showRedDot : (hasNotifications && announcementsViewed !== true && hasAnonymousUnread);
    const previousIsLoggedInRef = useRef(isLoggedIn);

    // Cross-tab sync: when another tab marks viewed, update local viewed state.
    React.useEffect(() => {
        const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('announcements_sync') : null;
        const handleMessage = (event: MessageEvent) => {
            const { type, referenceTimestamp, hasViewed, newAnnouncementIds } = event.data || {};

            if (type === 'VIEWED') {
                setAnnouncementsViewed(typeof hasViewed === 'boolean' ? hasViewed : null);
                return;
            }

            if (type === 'INIT_RESPONSE') {
                setAnnouncementsViewed(typeof hasViewed === 'boolean' ? hasViewed : null);
                if (Array.isArray(newAnnouncementIds)) {
                    setCurrentSessionNewIds(newAnnouncementIds);
                }
                return;
            }

            if (type === 'ANON_NEW_IDS' && !isLoggedIn) {
                if (Array.isArray(newAnnouncementIds)) {
                    setCurrentSessionNewIds(newAnnouncementIds);
                }
                return;
            }

            if (type === 'ANON_REF_REQUEST' && !isLoggedIn && hasAnonymousSessionReferenceLock()) {
                if (channel) {
                    channel.postMessage({ type: 'ANON_REF_RESPONSE', referenceTimestamp: loadAnonymousSessionReferenceTimestamp() });
                }
                return;
            }

            if (type === 'ANON_REF_RESPONSE' && !isLoggedIn) {
                if (referenceTimestamp === null || typeof referenceTimestamp === 'number') {
                    saveAnonymousSessionReferenceTimestamp(referenceTimestamp);
                    setAnonReferenceTimestamp(referenceTimestamp);
                }
            }
        };

        if (channel) { channel.addEventListener('message', handleMessage); }

        if (channel && !isLoggedIn) {
            channel.postMessage({ type: 'ANON_REF_REQUEST' });
        }

        return () => {
            if (channel) { channel.removeEventListener('message', handleMessage); channel.close(); }
        };
    }, [isLoggedIn]);

    React.useEffect(() => {
        const wasLoggedIn = previousIsLoggedInRef.current;

        if (wasLoggedIn && !isLoggedIn) {
            const hasSessionReferenceLock = hasAnonymousSessionReferenceLock();
            const referenceTimestamp = hasSessionReferenceLock
                ? loadAnonymousSessionReferenceTimestamp()
                : loadAnonymousLastOpenedTimestamp();

            saveAnonymousSessionReferenceTimestamp(referenceTimestamp);
            setAnonReferenceTimestamp(referenceTimestamp);
            setCurrentSessionNewIds(loadAnonymousCurrentSessionNewIds());

            if (!hasSessionReferenceLock) {
                setAnnouncementsViewed(null);
                previousIsLoggedInRef.current = isLoggedIn;
                return;
            }

            const hasUnreadAfterLogout = notifications.some((item: Announcement) => isGuestAnnouncementNew(item, referenceTimestamp));

            setAnnouncementsViewed(!hasUnreadAfterLogout);
            saveAnnouncementViewed(!hasUnreadAfterLogout);
        }

        previousIsLoggedInRef.current = isLoggedIn;
    }, [isLoggedIn, notifications]);

    React.useEffect(() => {
        if (isLoggedIn) {
            setCurrentSessionNewIds([]);
            return;
        }

        setCurrentSessionNewIds(loadAnonymousCurrentSessionNewIds());
    }, [isLoggedIn]);

    const adjust = () => adjustPanelPosition(panelRef, iconRef, setPanelStyle);

    useClickAway(panelRef, () => {
        if (panelOpen) {
            setPanelOpen(false);
        }
    });

    const handleBellClick = () => {
        const nextPanelOpen = !panelOpen;
        dispatch(markAnnouncementsViewed());
        if (!isLoggedIn) {
            setAnnouncementsViewed(true);
            if (nextPanelOpen) {
                const referenceTimestamp = lockAnonymousSessionReferenceTimestamp();
                setAnonReferenceTimestamp(referenceTimestamp);

                const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('announcements_sync') : null;
                if (channel) {
                    channel.postMessage({ type: 'ANON_REF_RESPONSE', referenceTimestamp });
                    channel.close();
                }
            }
        }
        if (!panelOpen) {
            setPanelStyle({
                visibility: 'hidden',
                left: '-9999px',
            } as React.CSSProperties);
        }
        setPanelOpen(nextPanelOpen);
        setTimeout(adjust, 0);
    };

    const handleDismiss = (id: string) => {
        if (!id) {
            return;
        }

        dispatch(dismissAnnouncements([id]));
    };

    const handleDismissAll = () => {
        if (!hasNotifications) {
            return;
        }

        dispatch(dismissAnnouncements(notifications.map((item: Announcement) => item.KeyMarketplaceAnnouncements)));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setPanelOpen(false);
        }
    };

    const handleViewHistory = () => {
        setPanelOpen(false);
        history.push(`/${langCode}/support/whats-new`, { source: 'panel_footer' });
    };

    React.useEffect(() => {
        if (!panelOpen) {
            return undefined;
        }

        const iconButton = iconRef.current ? iconRef.current.querySelector('button') : null;
        window.addEventListener('resize', adjust);
        if (iconButton) {
            iconButton.addEventListener('mouseenter', adjust);
            iconButton.addEventListener('focus', adjust);
        }
        adjust();

        return () => {
            window.removeEventListener('resize', adjust);
            if (iconButton) {
                iconButton.removeEventListener('mouseenter', adjust);
                iconButton.removeEventListener('focus', adjust);
            }
        };
    }, [panelOpen]);

    return (
        <div className={`${wn} ${!isLoggedIn ? `${wn}--logged-out` : ''}`} ref={panelRef} role="presentation" onKeyDown={handleKeyDown}>
            <div ref={iconRef}>
                <AnnouncementIcon hasUnread={hasUnread} panelOpen={panelOpen} onClick={handleBellClick} />
            </div>
            {panelOpen && (
                <div
                    className={`${wn}__panel ${!isLoggedIn ? `${wn}__panel-logged-out` : ''}`}
                    style={panelStyle}
                    role="dialog"
                    aria-label="What's New"
                    aria-modal="false"
                >
                    <div className={`${wn}__panel-header`}>
                        <h2 className={`${wn}__panel-title`}>What&apos;s New</h2>
                        <button
                            type="button"
                            className={`${wn}__dismiss-all`}
                            onClick={handleDismissAll}
                            disabled={!hasNotifications}
                            tabIndex={0}
                        >
                            Dismiss All
                        </button>
                    </div>
                    {!hasNotifications ? (
                        <EmptyState wn={wn} onViewHistory={() => setPanelOpen(false)} />
                    ) : (
                        <ul className={`${wn}__list  ${wn}__panel-scroll`}>
                            {notifications.map((item: Announcement) => (
                                <AnnouncementCard
                                    key={item.KeyMarketplaceAnnouncements}
                                    notification={item}
                                    onDismiss={handleDismiss}
                                    isNew={!isLoggedIn && isGuestAnnouncementNew(item)}
                                    useBackendHighlight={isLoggedIn}
                                />
                            ))}
                        </ul>
                    )}
                    {hasNotifications && (
                        <div className={`${wn}__panel-footer`}>
                            View announcement history <button type="button" onClick={handleViewHistory}>here</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
export default React.memo(WhatsNewPanel);
